import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Award, FileCheck, Globe, Truck, Wheat, ChevronRight } from "lucide-react";
import Marquee from "react-fast-marquee";
import { useLanguage } from "../context/LanguageContext";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HERO_IMG = "https://customer-assets.emergentagent.com/job_premium-rice-hub-3/artifacts/g7mfdr6k_B29EC347-7C41-4D24-8787-CDE8FD337956.PNG";
const RICE_FIELD = "https://images.unsplash.com/photo-1761549849686-d1fd83a094aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxnb2xkZW4lMjByaWNlJTIwZmllbGR8ZW58MHx8fHwxNzc2NDE1NjcwfDA&ixlib=rb-4.1.0&q=85";
const WORLD_MAP = "https://static.prod-images.emergentagent.com/jobs/82e52e3c-f998-4a3d-91a1-430c4242e074/images/6dc2c9ca1cc842001a87b5edb5f282be4c30651eb7ed1e11710ab63b538563da.png";
const TEXTURE = "https://static.prod-images.emergentagent.com/jobs/82e52e3c-f998-4a3d-91a1-430c4242e074/images/4965677917ee858333b841b058eac8cf4efd19c5c371c1eb957513b17771e8ef.png";

const stats = [
  { number: "50+", key: "stat_varieties" },
  { number: "15+", key: "stat_countries" },
  { number: "50K+", key: "stat_tonnes" },
  { number: "100%", key: "stat_quality" },
];

const testimonials = [
  {
    quote: "P.S.IMPEX consistently delivers premium quality rice. Their 1121 Basmati is the finest we've sourced from India.",
    author: "Ibrahim Al-Hassan",
    role: "Distributor, West Africa",
  },
  {
    quote: "Reliable, transparent, and always on time. Fab Crops has been our trusted partner for over three years now.",
    author: "David Okonkwo",
    role: "Import Manager, Lagos",
  },
  {
    quote: "The quality of golden sella rice from P.S.IMPEX is unmatched. Our customers love the aroma and grain length.",
    author: "Amir Patel",
    role: "Wholesaler, Toronto",
  },
];

export default function HomePage() {
  const { t } = useLanguage();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center py-32">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0}
            className="text-sm uppercase tracking-[0.3em] text-brand-gold font-sans font-semibold mb-6"
          >
            TRUSTED MILLERS & EXPORTERS OF PREMIUM INDIAN RICE WORLDWIDE
          </motion.p>
          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={1}
            className="font-sans text-7xl sm:text-8xl lg:text-[10rem] font-extrabold text-brand-bone leading-[0.9] mb-8 uppercase tracking-wider hidden"
          >
            FAB CROPS
          </motion.h1>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={2}
            className="text-brand-bone/80 text-base sm:text-lg font-sans max-w-xl mx-auto mb-10 leading-relaxed"
          >
            {t("hero_desc")}
          </motion.p>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={3}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              to="/products"
              data-testid="hero-explore-btn"
              className="group flex items-center gap-2 px-8 py-4 bg-brand-gold text-brand-green font-sans font-semibold text-sm rounded-full hover:bg-brand-bone transition-colors duration-300"
            >
              {t("hero_explore")}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-btn"
              className="flex items-center gap-2 px-8 py-4 border border-brand-bone/30 text-brand-bone font-sans font-medium text-sm rounded-full hover:bg-brand-bone/10 transition-colors duration-300"
            >
              {t("hero_quote")}
            </Link>
          </motion.div>
        </div>

        {/* Stats Bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-brand-green/90 backdrop-blur-md border-t border-brand-gold/20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, i) => (
                <motion.div
                  key={stat.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 + i * 0.1, duration: 0.6 }}
                  className="text-center"
                >
                  <p className="font-serif text-3xl lg:text-4xl font-light text-brand-gold">
                    {stat.number}
                  </p>
                  <p className="text-brand-bone/70 text-xs font-sans uppercase tracking-wider mt-1">
                    {t(stat.key)}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section data-testid="about-teaser" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
                Heritage & Trust
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-green leading-tight mb-6">
                Rooted in Quality,{" "}
                <span className="italic text-brand-gold">Grown</span> with Pride
              </h2>
              <p className="text-brand-muted text-base font-sans leading-relaxed mb-6">
                P.S.IMPEX, led by founder Manjot Singh, has established itself as
                a premier rice exporter from India. Our brand Fab Crops represents
                the finest selection of Basmati and Non-Basmati rice, sourced
                directly from the lush paddy fields of Punjab.
              </p>
              <div className="bg-brand-gold/10 border border-brand-gold/30 rounded-xl p-5 mb-6">
                <p className="text-brand-green font-sans font-semibold text-sm leading-relaxed">
                  Built on trust, we are India's first company to offer a 100% rice quality guarantee — backed by a full refund promise.
                </p>
              </div>
              <p className="text-brand-muted text-base font-sans leading-relaxed mb-8">
                With certifications from FSSAI, APEDA, and ISO, we ensure every
                grain meets the highest international standards. Our commitment to
                organic practices and sustainable farming sets us apart.
              </p>
              <Link
                to="/about"
                data-testid="about-teaser-link"
                className="inline-flex items-center gap-2 text-brand-green font-sans font-semibold text-sm hover:text-brand-gold transition-colors group"
              >
                Learn Our Story
                <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(26,54,38,0.12)]">
                <img
                  src={RICE_FIELD}
                  alt="Golden rice fields in Punjab"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-brand-green text-brand-bone p-6 rounded-2xl shadow-xl">
                <p className="font-serif text-2xl font-light text-brand-gold">Established</p>
                <p className="font-serif text-4xl font-semibold">2021</p>
                <p className="text-xs uppercase tracking-widest text-brand-bone/60 font-sans mt-1">Punjab, India</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PRODUCTS OVERVIEW */}
      <section
        data-testid="products-overview"
        className="py-24 lg:py-32"
        style={{
          backgroundImage: `url(${TEXTURE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Our Collection
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-green">
              Premium Rice Varieties
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Basmati Card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Link
                to="/products"
                data-testid="basmati-card"
                className="group block bg-white rounded-3xl overflow-hidden shadow-[0_8px_32px_rgba(26,54,38,0.06)] border border-brand-gold/10 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(26,54,38,0.1)] transition-all duration-500"
              >
                <div className="p-10 lg:p-12">
                  <div className="flex items-center gap-3 mb-6">
                    <Wheat className="text-brand-gold" size={28} />
                    <span className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold">
                      Premium Selection
                    </span>
                  </div>
                  <h3 className="font-serif text-3xl sm:text-4xl font-medium text-brand-green mb-4">
                    Basmati Rice
                  </h3>
                  <p className="text-brand-muted font-sans text-base leading-relaxed mb-6">
                    The crown jewel of Indian rice exports. Long-grain, aromatic,
                    and perfectly aged. Available in 1121, 1718, 1509, 1401 & Pusa
                    varieties.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-8">
                    {["1121", "1718", "1509", "1401", "Pusa"].map((v) => (
                      <span
                        key={v}
                        className="px-3 py-1 bg-brand-sand text-brand-green text-xs font-sans font-medium rounded-full"
                      >
                        {v}
                      </span>
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-2 text-brand-green font-sans font-semibold text-sm group-hover:text-brand-gold transition-colors">
                    View All Varieties
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </motion.div>

            {/* Pesticide Residue Free Card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <Link
                to="/products"
                data-testid="pesticide-free-card"
                className="group block bg-white rounded-3xl overflow-hidden shadow-[0_8px_32px_rgba(26,54,38,0.06)] border border-green-200/50 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(26,54,38,0.1)] transition-all duration-500"
              >
                <div className="p-10 lg:p-12">
                  <div className="flex items-center gap-3 mb-6">
                    <ShieldCheck className="text-green-600" size={28} />
                    <span className="text-sm uppercase tracking-[0.2em] text-green-600 font-sans font-semibold">
                      Certified Safe
                    </span>
                  </div>
                  <h3 className="font-serif text-3xl sm:text-4xl font-medium text-brand-green mb-4">
                    Pesticide Residue Free
                  </h3>
                  <p className="text-brand-muted font-sans text-base leading-relaxed mb-6">
                    Certified PRF rice for health-conscious global markets.
                    Meeting the strictest international food safety standards.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-8">
                    {["1121", "1718", "1509", "Pusa", "Sugandha", "Sharbati"].map((v) => (
                      <span
                        key={v}
                        className="px-3 py-1 bg-green-50 text-green-700 text-xs font-sans font-medium rounded-full"
                      >
                        {v}
                      </span>
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-2 text-brand-green font-sans font-semibold text-sm group-hover:text-green-600 transition-colors">
                    View PRF Varieties
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </motion.div>

            {/* Non-Basmati Card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <Link
                to="/products"
                data-testid="non-basmati-card"
                className="group block bg-brand-green rounded-3xl overflow-hidden shadow-[0_8px_32px_rgba(26,54,38,0.15)] hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(26,54,38,0.2)] transition-all duration-500"
              >
                <div className="p-10 lg:p-12">
                  <div className="flex items-center gap-3 mb-6">
                    <Wheat className="text-brand-gold" size={28} />
                    <span className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold">
                      Everyday Excellence
                    </span>
                  </div>
                  <h3 className="font-serif text-3xl sm:text-4xl font-medium text-brand-bone mb-4">
                    Non-Basmati Rice
                  </h3>
                  <p className="text-brand-bone/70 font-sans text-base leading-relaxed mb-6">
                    High-quality non-basmati rice varieties for bulk consumption and
                    industrial use. Consistent quality, competitive pricing.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-8">
                    {["IR64", "PR11", "Sona Masoori", "Parboiled", "Broken"].map((v) => (
                      <span
                        key={v}
                        className="px-3 py-1 bg-brand-bone/10 text-brand-bone text-xs font-sans font-medium rounded-full"
                      >
                        {v}
                      </span>
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-2 text-brand-gold font-sans font-semibold text-sm group-hover:text-brand-bone transition-colors">
                    View All Varieties
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* EXPORT HIGHLIGHTS */}
      <section
        data-testid="export-section"
        className="py-24 lg:py-32 bg-brand-green relative overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-15 bg-cover bg-center"
          style={{ backgroundImage: `url(${WORLD_MAP})` }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
                Global Presence
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-bone leading-tight mb-6">
                Exporting Trust{" "}
                <span className="italic text-brand-gold">Worldwide</span>
              </h2>
              <p className="text-brand-bone/70 text-base font-sans leading-relaxed mb-10">
                As a recognized high-volume exporter, P.S.IMPEX ships premium Indian
                rice to multiple continents. Our robust supply chain ensures timely
                delivery while maintaining uncompromised quality standards.
              </p>
              <div className="flex flex-col gap-6">
                {[
                  { icon: Globe, title: "Africa", desc: "Major markets across West and East Africa" },
                  { icon: Truck, title: "Indonesia", desc: "Consistent supply to Indonesian distributors" },
                  { icon: Globe, title: "Canada", desc: "Serving the growing South Asian diaspora" },
                  { icon: Globe, title: "Europe", desc: "Expanding presence across European markets" },
                  { icon: Globe, title: "Middle East", desc: "Trusted supplier to Gulf & Middle Eastern importers" },
                ].map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-brand-gold/10 flex items-center justify-center shrink-0">
                      <Icon size={20} className="text-brand-gold" />
                    </div>
                    <div>
                      <h4 className="font-serif text-xl font-medium text-brand-bone">{title}</h4>
                      <p className="text-brand-bone/60 text-sm font-sans">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="hidden lg:flex items-center justify-center"
            >
              <img
                src={WORLD_MAP}
                alt="Export destinations world map"
                className="w-full max-w-lg opacity-80"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS MARQUEE */}
      <section data-testid="certifications-marquee" className="py-12 bg-brand-sand">
        <Marquee speed={40} gradient={false} className="overflow-hidden">
          {[
            { icon: ShieldCheck, text: "FSSAI Certified" },
            { icon: Award, text: "APEDA Registered Exporter" },
            { icon: FileCheck, text: "ISO 9001:2015 Certified" },
            { icon: Globe, text: "LEI Registered" },
            { icon: Wheat, text: "Premium Quality Rice" },
            { icon: Globe, text: "Trusted Global Exporter" },
            { icon: ShieldCheck, text: "FSSAI Certified" },
            { icon: Award, text: "APEDA Registered Exporter" },
            { icon: FileCheck, text: "ISO 9001:2015 Certified" },
            { icon: Globe, text: "LEI Registered" },
          ].map(({ icon: Icon, text }, i) => (
            <div key={i} className="flex items-center gap-3 mx-10">
              <Icon size={18} className="text-brand-gold" />
              <span className="font-serif text-lg text-brand-green/80 whitespace-nowrap">
                {text}
              </span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* TESTIMONIALS */}
      <section
        data-testid="testimonials-section"
        className="py-24 lg:py-32"
        style={{
          backgroundImage: `url(${TEXTURE})`,
          backgroundSize: "cover",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Client Voices
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-green">
              Trusted by Importers
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.15 }}
                className="bg-white p-8 rounded-3xl shadow-[0_8px_32px_rgba(26,54,38,0.04)] border border-brand-gold/10"
              >
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, j) => (
                    <span key={j} className="text-brand-gold text-lg">&#9733;</span>
                  ))}
                </div>
                <p className="text-brand-text font-sans text-base leading-relaxed mb-6 italic">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div>
                  <p className="font-serif text-lg font-medium text-brand-green">
                    {t.author}
                  </p>
                  <p className="text-brand-muted text-sm font-sans">{t.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section data-testid="cta-section" className="py-24 lg:py-32 bg-brand-green">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Partner With Us
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-bone leading-tight mb-6">
              Ready to Source Premium Rice?
            </h2>
            <p className="text-brand-bone/70 text-base font-sans max-w-2xl mx-auto mb-10 leading-relaxed">
              Connect with P.S.IMPEX for competitive pricing, reliable supply,
              and the finest quality Indian rice for your markets.
            </p>
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="inline-flex items-center gap-2 px-10 py-4 bg-brand-gold text-brand-green font-sans font-semibold text-sm rounded-full hover:bg-brand-bone transition-colors duration-300"
            >
              Get in Touch
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
